// frontend/source/portal/pages/AdminArtistArtwork.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import adminApi from "../../services/adminapi";
import api from "../../services/api";
import CreateArtworkCard from "../components/CreateArtworkCard";
import EditArtworkCard from "../components/EditArtworkCard";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import ConfirmDialog from "../components/ConfirmDialog";
import "./AdminArtistArtwork.css";

const AdminArtistArtwork = () => {
  const { artistId } = useParams();
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState({
    isOpen: false,
    artwork: null,
  });

  useEffect(() => {
    loadArtistAndArtworks();
  }, [artistId]);

  const loadArtistAndArtworks = async () => {
    try {
      setLoading(true);
      const [artistData, artworksData] = await Promise.all([
        api.getArtistById(artistId),
        api.getArtworksByArtist(artistId, 1, 0, false),
      ]);
      setArtist(artistData);
      setArtworks(artworksData.artworks || artworksData);
      setError(null);
    } catch (err) {
      setError("Failed to load artist artworks. Please try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (artworkData) => {
    try {
      // Add artistId to the artwork data
      const dataWithArtist = {
        ...artworkData,
        artistId: artistId,
      };
      const newArtwork = await adminApi.createArtwork(dataWithArtist);
      setArtworks([newArtwork, ...artworks]);
      setError(null);
    } catch (err) {
      setError("Failed to create artwork. Please try again.");
      console.error(err);
      throw err;
    }
  };

  const handleAddExisting = async (artworkId) => {
    try {
      // Link existing artwork to this artist
      await adminApi.linkArtworkToArtist(artworkId, artistId);
      // Reload artworks to get the updated list
      const artworksData = await adminApi.getArtworksByArtist(artistId, 1, 0, false);
      setArtworks(artworksData.artworks || artworksData);
      setError(null);
    } catch (err) {
      setError("Failed to add existing artwork. Please try again.");
      console.error(err);
      throw err;
    }
  };

  const handleRemove = async (artworkId) => {
    const artwork = artworks.find((art) => art._id === artworkId);
    setRemoveConfirm({ isOpen: true, artwork });
  };

  const confirmRemove = async () => {
    try {
      // Unlink artwork from artist (or delete if this is the only artist)
      await adminApi.unlinkArtworkFromArtist(
        removeConfirm.artwork._id,
        artistId
      );
      setArtworks(
        artworks.filter((art) => art._id !== removeConfirm.artwork._id)
      );
      setRemoveConfirm({ isOpen: false, artwork: null });
    } catch (err) {
      setError("Failed to remove artwork. Please try again.");
      console.error(err);
      setRemoveConfirm({ isOpen: false, artwork: null });
    }
  };

  const cancelRemove = () => {
    setRemoveConfirm({ isOpen: false, artwork: null });
  };

  const handleUpdate = async (artworkId, updateData) => {
    try {
      const updated = await adminApi.updateArtwork(artworkId, updateData);
      setArtworks(
        artworks.map((art) => (art._id === artworkId ? updated : art))
      );
    } catch (err) {
      setError("Failed to update artwork. Please try again.");
      console.error(err);
      throw err;
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading artist artworks..." />;
  }

  if (error && !artist) {
    return <ErrorMessage message={error} onRetry={loadArtistAndArtworks} />;
  }

  const getArtistDisplayName = () => {
    return artist?.fullName || `${artist?.fname} ${artist?.lname}`;
  };

  return (
    <div className="admin-artist-artwork-main">
      <div className="admin-artist-artwork-content">
        {/* Artist Header */}
        <div className="admin-artist-header">
          <div className="admin-artist-header-image-container">
            <img
              src={artist?.imageUrl || "/images/default-artist.jpg"}
              alt={getArtistDisplayName()}
              className="admin-artist-header-image"
            />
          </div>
          <div className="admin-artist-header-info">
            <h1 className="admin-artist-header-title">
              {getArtistDisplayName()}
            </h1>
            <p className="admin-artist-header-subtitle">Manage Artworks</p>
            {artist?.birthdate && (
              <p className="admin-artist-header-detail">
                Born: {new Date(artist.birthdate).toLocaleDateString()}
              </p>
            )}
            {artist?.gender && (
              <p className="admin-artist-header-detail">
                Gender:{" "}
                {artist.gender.charAt(0).toUpperCase() + artist.gender.slice(1)}
              </p>
            )}
          </div>
        </div>

        {error && <div className="admin-artist-artwork-error">{error}</div>}

        <div className="admin-artworks-grid">
          <CreateArtworkCard
            onCreate={handleCreate}
            onAddExisting={handleAddExisting}
            artistId={artistId}
          />
          {artworks.map((artwork) => (
            <EditArtworkCard
              key={artwork._id}
              artwork={artwork}
              onRemove={handleRemove}
              onUpdate={handleUpdate}
              artistId={artistId}
            />
          ))}
        </div>
      </div>

      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title="Remove Artwork?"
        message={`Are you sure you want to remove "${
          removeConfirm.artwork?.title || ""
        }" from this artist? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        type="warning"
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
      />
    </div>
  );
};

export default AdminArtistArtwork;
